import { useLingui } from "@lingui/react/macro";
import { ChevronDownIcon } from "lucide-react";
import type { ReactNode } from "react";

import { HeroAvatar, useHeroLookup, useRankLookup } from "@/features/matches";
import { cn } from "@/shared/lib";

import type { FriendsMetric } from "../../model/FRIENDS_METRICS";
import type { FriendPlayer } from "../../model/FriendsBoard";
import { formatMetric } from "../../model/formatMetric";

// Колонки строки и заголовка: место, игрок, ранг, матчи, показатель, неделя, стрелка.
export const ROW_CLASSNAME =
  "grid grid-cols-[44px_minmax(0,2fr)_150px_70px_minmax(0,2fr)_80px_28px] items-center gap-3";

const MEDALS = ["text-accent", "text-foreground-soft", "text-warn"];

type FriendRowProps = {
  player: FriendPlayer;
  isMe: boolean;
  place: number | null;
  value: number | null;
  metric: FriendsMetric;
  // Ширина полосы показателя, 0..100.
  bar: number;
  move: number | null;
  // Только у друга: раскрыт ли сравнение и как его переключить.
  expanded?: boolean;
  panelId?: string;
  onToggle?: () => void;
};

// Строка таблицы: у тебя — просто строка, у друга — кнопка, которая раскрывает сравнение.
export const FriendRow = ({
  player,
  isMe,
  place,
  value,
  metric,
  bar,
  move,
  expanded = false,
  panelId,
  onToggle,
}: FriendRowProps) => {
  const { i18n, t } = useLingui();
  const heroLookup = useHeroLookup();
  const rankLookup = useRankLookup();
  const hero = player.main_hero_id === null ? null : heroLookup(player.main_hero_id);
  const badge = player.current.rank_badge;
  const rank = badge === null ? null : rankLookup(badge);
  const name = isMe ? t`You` : (player.display_name ?? t`Friend`);
  const idle = player.current.matches === 0;
  let shown = formatMetric(metric, value, i18n.locale);
  if (idle) {
    shown = t`no matches`;
  } else if (metric === "rank" && rank !== null) {
    shown = rank.name;
  }

  const cells: ReactNode = (
    <>
      <span
        className={cn(
          "font-mono text-lg font-semibold",
          place === null ? "text-subtle" : (MEDALS[place - 1] ?? "text-subtle"),
        )}
      >
        {place ?? "—"}
      </span>
      <span className="flex min-w-0 items-center gap-2.5">
        {hero === null ? (
          <span aria-hidden="true" className="size-9 shrink-0 rounded-lg bg-surface-raised" />
        ) : (
          <HeroAvatar name={hero.name} iconUrl={hero.iconUrl} size={36} />
        )}
        <span className="flex min-w-0 flex-col">
          <span className="truncate font-semibold">{name}</span>
          {hero !== null && <span className="text-xs text-subtle">{hero.name}</span>}
        </span>
      </span>
      <span className="flex items-center gap-2 text-sm text-muted">
        {rank !== null && rank.iconUrl !== null && (
          <img src={rank.iconUrl} alt="" width={28} height={28} className="size-7 object-contain" />
        )}
        {rank?.name ?? "—"}
      </span>
      <span className="font-mono text-sm text-muted">{player.current.matches}</span>
      <span className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className={cn(
            "block h-2.5 flex-auto overflow-hidden rounded",
            isMe ? "bg-background" : "bg-sidebar-active",
          )}
        >
          <span
            className={cn("block h-full", isMe ? "bg-accent" : "bg-neutral")}
            style={{ width: `${String(bar)}%` }}
          />
        </span>
        <span
          className={cn(
            "min-w-24 text-right font-mono",
            idle ? "text-[13px] text-subtle" : "font-semibold",
          )}
        >
          {shown}
        </span>
      </span>
      <span
        className={cn(
          "text-right font-mono text-sm",
          move !== null && move > 0
            ? "text-good"
            : move !== null && move < 0
              ? "text-bad"
              : "text-subtle",
        )}
      >
        {move === null || move === 0 ? "—" : move > 0 ? `▲ ${String(move)}` : `▼ ${String(-move)}`}
      </span>
    </>
  );

  if (isMe || onToggle === undefined) {
    return (
      <div
        data-row="me"
        className={cn(
          ROW_CLASSNAME,
          "rounded-[10px] border border-border-strong bg-sidebar-active px-3 py-2",
        )}
      >
        {cells}
        <span />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-controls={panelId}
      className={cn(
        ROW_CLASSNAME,
        "w-full cursor-pointer rounded-[10px] border px-3 py-2 text-left transition-colors hover:border-border-strong",
        expanded ? "border-border-strong bg-surface-raised" : "border-sidebar-active bg-sidebar",
        idle && "opacity-70",
      )}
    >
      {cells}
      <ChevronDownIcon
        aria-hidden="true"
        className={cn("size-5 text-accent transition-transform", expanded && "rotate-180")}
      />
    </button>
  );
};
